/**
 * Colossus Gate — entrada selada para a arena do Aether Colossus (fim das Warden Grounds).
 * Rochas gigantes + cristais + runas + energia Aether. SELADO (apagado) até derrotar 3
 * Shard Wardens e descobrir as 4 sub-áreas; então acende e libera o USAR. Só existe na R2.
 */
import * as THREE from "three";
import { crystalStdMat, energyMat, stoneMat } from "./Materials";

export class ColossusGate {
  readonly group = new THREE.Group();
  private active = false;
  private pulse = 0;
  // 10.9BQ — clones da biblioteca (pulsam/ligam-desligam)
  private readonly crystalMats: THREE.MeshStandardMaterial[] = [];
  private readonly runeMat: THREE.MeshBasicMaterial;
  private readonly barrierMat: THREE.MeshBasicMaterial;
  private readonly light: THREE.PointLight;

  constructor(scene: THREE.Scene, x: number, z: number) {
    const rockMat = stoneMat(0x3f4d63);

    // duas rochas gigantes formando a passagem
    const boulderGeo = new THREE.DodecahedronGeometry(3, 0);
    for (const bx of [-4.5, 4.5]) {
      const b = new THREE.Mesh(boulderGeo, rockMat);
      b.position.set(bx, 2.4, 0);
      b.scale.set(1, 1.8, 1.1);
      b.rotation.y = bx < 0 ? 0.4 : -0.4;
      b.castShadow = true;
      this.group.add(b);
    }
    // laje superior (arco de pedra)
    const top = new THREE.Mesh(new THREE.BoxGeometry(10, 1.4, 2.4), rockMat);
    top.position.set(0, 6.6, 0);
    top.castShadow = true;
    this.group.add(top);

    // cristais integrados
    const cryGeo = new THREE.OctahedronGeometry(0.7, 0);
    const cm = crystalStdMat(0x62e8ff).clone(); // um clone pros 5 cristais
    this.crystalMats.push(cm);
    for (const [cx, cy, s] of [[-4.5, 5, 1.2], [4.5, 5, 1.2], [0, 7.6, 1.5], [-4.5, 2.5, 0.9], [4.5, 2.5, 0.9]]) {
      const c = new THREE.Mesh(cryGeo, cm);
      c.position.set(cx, cy, 0);
      c.scale.setScalar(s);
      this.group.add(c);
    }
    // fragmentos flutuantes discretos
    const fm = crystalStdMat(0x8fe8ff).clone();
    this.crystalMats.push(fm);
    for (let i = 0; i < 4; i++) {
      const f = new THREE.Mesh(new THREE.OctahedronGeometry(0.3, 0), fm);
      f.position.set((Math.random() - 0.5) * 6, 3 + Math.random() * 3, 0.2);
      this.group.add(f);
    }

    // barreira selada (véu ESCURO entre as rochas — não é energia aditiva, é o selo; segue custom)
    this.barrierMat = new THREE.MeshBasicMaterial({ color: 0x2a3d5c, transparent: true, opacity: 0.4, side: THREE.DoubleSide, depthWrite: false });
    const barrier = new THREE.Mesh(new THREE.PlaneGeometry(8, 6.5), this.barrierMat);
    barrier.position.set(0, 3.3, 0);
    this.group.add(barrier);

    // runa no chão (apagada enquanto selado)
    this.runeMat = energyMat(0x62e8ff, "ring").clone();
    const rune = new THREE.Mesh(new THREE.RingGeometry(2.2, 2.9, 6), this.runeMat);
    rune.rotation.x = -Math.PI / 2;
    rune.position.set(0, 0.05, 3.5);
    this.group.add(rune);

    this.light = new THREE.PointLight(0x62e8ff, 0, 20, 2);
    this.light.position.set(0, 4, 1);
    this.group.add(this.light);

    this.group.position.set(x, 0, z);
    scene.add(this.group);
    this.applyActive();
  }

  setActive(v: boolean): void {
    this.active = v;
    this.applyActive();
  }
  get isActive(): boolean {
    return this.active;
  }
  setVisible(v: boolean): void {
    this.group.visible = v;
  }
  private applyActive(): void {
    for (const m of this.crystalMats) m.emissiveIntensity = this.active ? 1.3 : 0.2;
    this.runeMat.opacity = this.active ? 0.45 : 0.12;
    this.barrierMat.opacity = this.active ? 0 : 0.4; // acesa = barreira some
    this.light.intensity = this.active ? 4 : 0;
  }

  isPlayerNear(pos: { x: number; z: number }, radius: number): boolean {
    const dx = pos.x - this.group.position.x;
    const dz = pos.z - this.group.position.z - 3.5; // toca perto da runa (frente do portão)
    return dx * dx + dz * dz <= radius * radius;
  }

  update(dt: number): void {
    if (!this.group.visible || !this.active) return;
    this.pulse += dt;
    const e = 1.1 + Math.sin(this.pulse * 2) * 0.4;
    for (const m of this.crystalMats) m.emissiveIntensity = e;
    this.light.intensity = 3.5 + Math.sin(this.pulse * 3) * 1;
  }
}
